<script lang="ts">
  import type { FeatureTracking, FeatureTrackingReference } from "../tracking";
  import MinMaxInput from "./custom-range/MinMaxInput.svelte";
  import SliderInput from "./custom-range/SliderInput.svelte";
  import type { Interval, FacetValue, FacetValueOptions } from "./type";
  import { SingleSelect } from "../facet-value-input";
  import { updateFeatureTrackingLabel } from "../tracking/FeatureTracking.ts";
  import { isActive } from "../../toggle/Toggle.ts";

  interface Props extends FacetValue, FacetValueOptions {
    selectedValues?: string;
    featureTracking?: FeatureTracking;
    availableRange: Required<Interval>;
    unit?: string;
    logarithmic?: boolean;
    useMinMax?: boolean;
    useSlider?: boolean;
    excludeFromTracking?: boolean;
    filterId?: string;
    popularValues?: FacetValue[];
  }

  let {
    id,
    name,
    checked = $bindable(false),
    selectedValues = $bindable(),
    range = $bindable({}),
    featureTracking,
    availableRange,
    unit,
    logarithmic = false,
    useMinMax = true,
    useSlider = false,
    excludeFromTracking = $bindable(false),
    filterId,
    popularValues,
  }: Props = $props();

  let value = $derived(`${range.from || "_"},${range.to || "_"}`);
  let isValid = $derived(
    !range.from || !range.to || range.from <= range.to || (range.from === undefined && range.to === undefined),
  );

  function oninput() {
    checked = true;
    excludeFromTracking = false;
  }

  function onchange() {
    if (popularValues && popularValues.length > 0 && featureTracking) {
      const equalsTopFacetValue = !!popularValues.find(
        (popVal) => popVal.range.from === range.from && popVal.range.to === range.to,
      );
      featureTracking = updateFeatureTrackingLabel(
        featureTracking,
        "san_TopFacetValue",
        equalsTopFacetValue ? "true" : "false",
      );
    }
    if (isActive("HEUREKA_1788_RANGE_VALIDATION")) {
      /*                                                                                  */
      setTimeout(() => {
        const event = new CustomEvent("heureka.customRange.validityChanged", {
          bubbles: true,
          detail: { filterId },
        });
        document.dispatchEvent(event);
      }, 0);
    }
  }
  if (isActive("HEUREKA_1788_RANGE_VALIDATION")) {
    document.addEventListener("heureka.filterMessageBanner.dismiss", (event) => {
      const customEvent = event as CustomEvent<{ filterId: string }>;
      const filterId = customEvent.detail?.filterId;
      /*                                                                   */
      if (filterId === filterId && !isValid) {
        range.from = undefined;
        range.to = undefined;
      }
    });
  }

  const featureTrackingReference: FeatureTrackingReference = {
    ref: id,
    labels: {},
  };
</script>

<div class="hc_customRange hc_facetValue" data-facet-name={name} data-filter-id={filterId} {oninput} {onchange}>
  <label hidden>
    <SingleSelect
      {id}
      {checked}
      class="hc_customRange__input"
      {name}
      {value}
      bind:selectedValues
      bind:featureTracking
      bind:excludeFromTracking
      {isValid}
    />
  </label>
  {#if useMinMax}
    <MinMaxInput {unit} bind:range {availableRange} {featureTrackingReference} {isValid} />
  {/if}
  {#if useSlider}
    <SliderInput bind:range {availableRange} {logarithmic} {featureTrackingReference} />
  {/if}
</div>

<style lang="scss">
  .hc_customRange {
    display: flex;
    flex-direction: column;
    gap: calc(var(--oc-semantic-spacing-150) - var(--oc-semantic-spacing-25));
  }
</style>
